import React from 'react';

import Box from '@amzn/meridian/box';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type BackgroundFormData } from 'src/store/newJmrPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { JmrFlowTypes } from 'src/utils/jmrUtils';

interface RestrictionInformationProps {
  handleChange: (key: keyof BackgroundFormData, value: any) => void;
}

const RestrictionInformation = ({ handleChange }: RestrictionInformationProps) => {
  const { backgroundFormData, backgroundFormErrors, jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const ppowFlows = [JmrFlowTypes.CreateNewAccommodation, JmrFlowTypes.NewRestrictions, JmrFlowTypes.CompleteTransfer];
  const isDisabled = !!backgroundFormData.providerPlacedOffWork;

  return (
    <Box>
      <Column spacing="200" spacingInset="400 none none none">
        <Text type="h300">Restriction Information</Text>
      </Column>
      <Row spacingInset="400 none 300 none" alignmentVertical="top">
        <DatePicker
          label="Restriction Start Date"
          id="new-jmr__background__restrictionStartDate"
          data-cy="new-jmr__background__restrictionStartDate"
          placeholder="MM/DD/YYYY"
          value={backgroundFormData.restrictionStartDate}
          onChange={(updatedValue) => handleChange('restrictionStartDate', updatedValue)}
          error={backgroundFormErrors.restrictionStartDate?.error}
          errorMessage={backgroundFormErrors.restrictionStartDate?.errorMessage}
        />

        {!backgroundFormData.indefiniteRestrictions && (
          <DatePicker
            label="Restriction End Date"
            id="new-jmr__background__restrictionEndDate"
            data-cy="new-jmr__background__restrictionEndDate"
            placeholder="MM/DD/YYYY"
            value={backgroundFormData.restrictionEndDate}
            onChange={(updatedValue) => handleChange('restrictionEndDate', updatedValue)}
            error={backgroundFormErrors.restrictionEndDate?.error}
            errorMessage={backgroundFormErrors.restrictionEndDate?.errorMessage}
          />
        )}
      </Row>

      <Row spacingInset="300 none" alignmentVertical="top">
        <Column spacing="400">
          {ppowFlows.includes(jmrFlowType) && (
            <Checkbox
              checked={backgroundFormData.providerPlacedOffWork}
              aria-label="Provider Placed Off Work"
              onChange={(newValue) => handleChange('providerPlacedOffWork', newValue)}
              data-cy="new-jmr__background__providerPlacedOffWork"
              disabled={
                backgroundFormData.indefiniteRestrictions ||
                backgroundFormData.cannotWorkMoreThan40Hrs ||
                backgroundFormData.cannotWorkNormalShiftHrs
              }
            >
              Provider Placed Off Work
            </Checkbox>
          )}
          <Checkbox
            checked={backgroundFormData.indefiniteRestrictions}
            aria-label="Permanent/Indefinite restrictions"
            onChange={(newValue) => handleChange('indefiniteRestrictions', newValue)}
            data-cy="new-jmr__background__indefiniteRestrictions"
            disabled={isDisabled}
          >
            Permanent/Indefinite restrictions
          </Checkbox>

          <Column spacing="200">
            <Checkbox
              checked={backgroundFormData.cannotWorkNormalShiftHrs}
              aria-label="Associate cannot work normal shift hours"
              onChange={(newValue) => handleChange('cannotWorkNormalShiftHrs', newValue)}
              data-cy="new-jmr__background__cannotWorkNormalShiftHrs"
              disabled={isDisabled}
            >
              Associate cannot work normal shift hours
            </Checkbox>
            {backgroundFormData.cannotWorkNormalShiftHrs && (
              <Input
                type="text"
                label="Limited Hours per Day"
                size="small"
                placeholder="Restricted shift hours"
                value={backgroundFormData.limitedHoursPerDay}
                onChange={(newValue) => {
                  handleChange('limitedHoursPerDay', newValue);
                }}
                error={backgroundFormErrors.limitedHoursPerDay?.error}
                errorMessage={backgroundFormErrors.limitedHoursPerDay?.errorMessage}
                data-cy="new-jmr__background__limitedHoursPerDay"
                disabled={isDisabled}
              />
            )}
          </Column>

          <Column spacing="200">
            <Checkbox
              checked={backgroundFormData.cannotWorkMoreThan40Hrs}
              aria-label="Associate cannot work 40+ hours a week"
              onChange={(newValue) => handleChange('cannotWorkMoreThan40Hrs', newValue)}
              data-cy="new-jmr__background__cannotWorkMoreThan40Hrs"
              disabled={isDisabled}
            >
              Associate cannot work 40+ hours a week
            </Checkbox>
            {backgroundFormData.cannotWorkMoreThan40Hrs && (
              <Input
                type="text"
                label="Limited Hours per Week"
                size="small"
                placeholder="Restricted shift hours"
                value={backgroundFormData.limitedHoursPerWeek}
                onChange={(newValue) => {
                  handleChange('limitedHoursPerWeek', newValue);
                }}
                error={backgroundFormErrors.limitedHoursPerWeek?.error}
                errorMessage={backgroundFormErrors.limitedHoursPerWeek?.errorMessage}
                data-cy="new-jmr__background__limitedHoursPerWeek"
                disabled={isDisabled}
              />
            )}
          </Column>
        </Column>
      </Row>
    </Box>
  );
};

export default RestrictionInformation;
